import ExcelJS from 'exceljs';
import { COMPANY, SHEET, TABLE } from './tokens';
import { formatPeriod, uniqueTableName } from './layout';
import { validateWorkbook, writeWorkbook } from './validator';
import { buildExecutiveSummarySheet } from './sheets/executiveSummary';
import { buildTransactionsLedgerSheet } from './sheets/transactionsLedger';
import { buildGstAnalysisSheet } from './sheets/gstAnalysis';
import { buildPaymentChannelsSheet } from './sheets/paymentChannels';
import { isMobileExportDevice } from './mobileCompat';
import { WALLET_TXN_CATEGORY } from '../../../ledger/mapWalletTransactions';

export { isMobileExportDevice } from './mobileCompat';

export async function buildEnterpriseWorkbook({ rows, summary, analytics, meta = {} }) {
  const debug = Boolean(meta.debugExport);
  const mobileCompat = Boolean(meta.mobileCompat ?? isMobileExportDevice());
  const reportId = meta.reportId || `REC-${Date.now().toString(36).toUpperCase()}`;
  const enrichedMeta = {
    ...meta,
    reportId,
    generatedBy: meta.generatedBy || COMPANY.module,
    mobileCompat,
  };

  const workbook = new ExcelJS.Workbook();
  workbook.creator = COMPANY.name;
  workbook.lastModifiedBy = enrichedMeta.generatedBy;
  workbook.created = new Date();
  workbook.modified = new Date();
  workbook.company = COMPANY.name;
  workbook.title = 'Financial Reconciliation Report';
  workbook.subject = 'Enterprise Reconciliation Export';
  workbook.description = `Reconciliation · ${formatPeriod(meta.from, meta.to)} · ${reportId}`;

  const usedTableNames = new Set();
  const tableNames = {
    clientTxns: uniqueTableName(TABLE.CLIENT_TXNS, usedTableNames),
    ownerTxns: uniqueTableName(TABLE.OWNER_TXNS, usedTableNames),
    treasuryTxns: uniqueTableName(TABLE.TREASURY_TXNS, usedTableNames),
    transactions: uniqueTableName(TABLE.TRANSACTIONS, usedTableNames),
    gst: uniqueTableName(TABLE.GST, usedTableNames),
    channels: uniqueTableName(TABLE.CHANNELS, usedTableNames),
    channelVolume: uniqueTableName(TABLE.CHANNEL_VOLUME, usedTableNames),
    audit: uniqueTableName(TABLE.AUDIT, usedTableNames),
  };

  const ctx = { rows, summary, analytics, meta: enrichedMeta };
  const clientRows = rows.filter((r) => r.category === WALLET_TXN_CATEGORY.CLIENT);
  const ownerRows = rows.filter((r) => r.category === WALLET_TXN_CATEGORY.OWNER);
  const treasuryRows = rows.filter(
    (r) => r.category === WALLET_TXN_CATEGORY.TREASURY
      || r.category === WALLET_TXN_CATEGORY.INTERNAL,
  );

  buildExecutiveSummarySheet(workbook, ctx);
  if (debug) validateWorkbook(workbook);

  buildTransactionsLedgerSheet(workbook, ctx, tableNames.clientTxns, {
    rows: clientRows,
    sheetName: SHEET.TRANSACTIONS_CLIENT,
    sheetTitle: 'Client Transactions',
  });
  if (debug) validateWorkbook(workbook);

  buildTransactionsLedgerSheet(workbook, ctx, tableNames.ownerTxns, {
    rows: ownerRows,
    sheetName: SHEET.TRANSACTIONS_OWNER,
    sheetTitle: 'Owner Transactions',
  });
  if (debug) validateWorkbook(workbook);

  buildTransactionsLedgerSheet(workbook, ctx, tableNames.treasuryTxns, {
    rows: treasuryRows,
    sheetName: SHEET.TRANSACTIONS_TREASURY,
    sheetTitle: 'Treasury & Internal',
  });
  if (debug) validateWorkbook(workbook);

  buildTransactionsLedgerSheet(workbook, ctx, tableNames.transactions, {
    rows,
    sheetName: SHEET.TRANSACTIONS_ALL,
    sheetTitle: 'All Transactions',
  });
  if (debug) validateWorkbook(workbook);

  buildGstAnalysisSheet(workbook, ctx, tableNames.gst);
  if (debug) validateWorkbook(workbook);

  buildPaymentChannelsSheet(workbook, ctx, tableNames.channels);
  const validation = validateWorkbook(workbook);

  const { buffer } = await writeWorkbook(workbook, { debug, mobileCompat });

  return {
    buffer,
    validation,
    reportId,
    sheetCount: 7,
    sheetNames: [
      SHEET.SUMMARY,
      SHEET.TRANSACTIONS_CLIENT,
      SHEET.TRANSACTIONS_OWNER,
      SHEET.TRANSACTIONS_TREASURY,
      SHEET.TRANSACTIONS_ALL,
      SHEET.GST,
      SHEET.CHANNELS,
    ],
    tableNames: Object.values(tableNames),
  };
}
